import { Route, Switch, useLocation } from "wouter";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { AppErrorBoundary } from "@/components/AppErrorBoundary";
import { ThemeProvider } from "@/components/ThemeProvider";
import { ToastProvider, useToast } from "@/hooks/use-toast";
import Auth from "@/pages/Auth";
import Signup from "@/pages/Signup";
import Reset from "@/pages/Reset";
import Plans from "@/pages/Plans";
import Workspace from "@/pages/Workspace";
import ComparisonWorkspace from "@/pages/ComparisonWorkspace";
import SupplementDetail from "@/pages/SupplementDetail";
import Claims from "@/pages/Claims";
import ClaimDetail from "@/pages/ClaimDetail";
import Tasks from "@/pages/Tasks";
import Templates from "@/pages/Templates";
import Automations from "@/pages/Automations";
import TemplateEditor from "@/pages/TemplateEditor";
import Settings from "@/pages/Settings";
import Financials from "@/pages/Financials";
import Directory from "@/pages/Directory";
import AcceptInvite from "@/pages/AcceptInvite";
import PublicFormSubmit from "@/pages/PublicFormSubmit";
import EmbedWidget from "@/pages/EmbedWidget";
import ProtectedRoute from "@/components/ProtectedRoute";
import { setNavigationFunction, setToastForRateLimiting } from "@/lib/api-client";
import { useEffect } from "react";

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
});

function AppRoutes() {
  const [, setLocation] = useLocation();
  const { toast } = useToast();

  // Set navigation function for API client error handling
  useEffect(() => {
    setNavigationFunction(setLocation);
  }, [setLocation]);

  // Set toast function for rate limit notifications
  useEffect(() => {
    setToastForRateLimiting(toast);
  }, [toast]);

  return (
    <Switch>
      <Route path="/login" component={Auth} />
      <Route path="/signup" component={Signup} />
      <Route path="/reset" component={Reset} />
      <Route path="/plans" component={Plans} />
      <Route path="/accept-invitation/:token" component={AcceptInvite} />
      <Route path="/forms/:accessToken" component={PublicFormSubmit} />
      <Route path="/embed/widget" component={EmbedWidget} />
      <Route path="/">
        <ProtectedRoute>
          <Workspace />
        </ProtectedRoute>
      </Route>
      <Route path="/claims/:claimId/comparison/:comparisonId">
        <ProtectedRoute>
          <ComparisonWorkspace />
        </ProtectedRoute>
      </Route>
      <Route path="/supplements/:id">
        <ProtectedRoute>
          <SupplementDetail />
        </ProtectedRoute>
      </Route>
      <Route path="/claims">
        <ProtectedRoute>
          <Claims />
        </ProtectedRoute>
      </Route>
      <Route path="/claims/:id">
        <ProtectedRoute>
          <ClaimDetail />
        </ProtectedRoute>
      </Route>
      <Route path="/tasks">
        <ProtectedRoute>
          <Tasks />
        </ProtectedRoute>
      </Route>
      <Route path="/templates">
        <ProtectedRoute>
          <Templates />
        </ProtectedRoute>
      </Route>
      <Route path="/templates/:id/editor">
        <ProtectedRoute>
          <TemplateEditor />
        </ProtectedRoute>
      </Route>
      <Route path="/automations">
        <ProtectedRoute>
          <Automations />
        </ProtectedRoute>
      </Route>
      <Route path="/financials">
        <ProtectedRoute>
          <Financials />
        </ProtectedRoute>
      </Route>
      <Route path="/settings">
        <ProtectedRoute skipSubscriptionCheck>
          <Settings />
        </ProtectedRoute>
      </Route>
      
      <Route path="/directory">
        <ProtectedRoute>
          <Directory />
        </ProtectedRoute>
      </Route>
      
      <Route>
        <div className="min-h-screen flex items-center justify-center bg-slate-50">
          <div className="text-center">
            <h1 className="text-4xl font-bold text-gray-900 mb-4">404</h1>
            <p className="text-gray-600">Page not found</p>
          </div>
        </div>
      </Route>
    </Switch>
  );
}

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <ThemeProvider defaultTheme="system" storageKey="vite-ui-theme">
        <ToastProvider>
          <AppErrorBoundary>
            <AppRoutes />
          </AppErrorBoundary>
        </ToastProvider>
      </ThemeProvider>
    </QueryClientProvider>
  );
}

export default App;
